import { MainTab } from '../../util/constants';

/**
 * Top-nav items including Admin and future tabs (not all in MainTab yet).
 * Ordered highest → lowest priority.
 */
export type NavItem = MainTab | 'ADMIN' | 'BILLING' | 'REWARDS';

/**
 * Product priority order (highest → lowest). Highest stays in the bar longest
 * as the viewport shrinks / runs out of horizontal space.
 * BILLING / REWARDS are reserved for when those tabs ship; until then they are
 * never in `permitted` and are filtered out by orderNavByPriority.
 */
export const TAB_PRIORITY: NavItem[] = [
  'CONFIGURATION', // Settings
  'AGENT_HUB',
  'INSIGHTS',
  'PAYLINKS',
  'CARD_ON_FILE',
  'BILLING',
  'REWARDS',
  'ADMIN',
];

export const orderNavByPriority = (permitted: NavItem[]): NavItem[] =>
  TAB_PRIORITY.filter((item) => permitted.includes(item));

export const splitNavByPriority = (
  permitted: NavItem[],
  visibleCount: number,
): { bar: NavItem[]; more: NavItem[] } => {
  const ordered = orderNavByPriority(permitted);
  const count = Math.max(0, visibleCount);
  return {
    bar: ordered.slice(0, count),
    more: ordered.slice(count),
  };
};

/**
 * How many leading items (already priority-ordered) fit in `availableWidth`,
 * reserving space for a More control when anything would overflow.
 */
export const countFittingNavItems = (
  itemWidths: number[],
  moreWidth: number,
  availableWidth: number,
  gap = 0,
): number => {
  if (availableWidth <= 0 || itemWidths.length === 0) return 0;

  let count = 0;
  let used = 0;

  for (let i = 0; i < itemWidths.length; i += 1) {
    const gapBefore = count > 0 ? gap : 0;
    const widthWithThis = used + gapBefore + itemWidths[i];
    const hasOverflowAfter = i < itemWidths.length - 1;
    const moreSpace = hasOverflowAfter ? gap + moreWidth : 0;

    if (widthWithThis + moreSpace <= availableWidth) {
      used = widthWithThis;
      count = i + 1;
    } else {
      break;
    }
  }

  return count;
};

/**
 * Soft cap below `sm` (600px). Tablets (e.g. iPad Mini 768) stay fit-by-width.
 * Cap of 4 keeps Paylinks in the bar; Card on file+ stay in More.
 */
export const MOBILE_BAR_SOFT_CAP = 4;

/**
 * Resolve visible bar count from measured widths.
 * Optional `maxVisible` is for mobile soft-cap only — do not use on desktop.
 * When widths are unmeasured (jsdom / first paint), show all (or up to maxVisible).
 */
export const resolveVisibleNavCount = ({
  itemWidths,
  moreWidth,
  availableWidth,
  gap,
  maxVisible,
}: {
  itemWidths: number[];
  moreWidth: number;
  availableWidth: number;
  gap: number;
  /** Mobile-only soft cap; omit on desktop so packing is fit-only. */
  maxVisible?: number;
}): number => {
  if (itemWidths.length === 0) return 0;

  const applyCap = (count: number) =>
    maxVisible != null
      ? Math.min(count, maxVisible, itemWidths.length)
      : Math.min(count, itemWidths.length);

  // jsdom / first paint: no usable width yet
  if (availableWidth <= 0 || itemWidths.every((w) => w <= 0)) {
    return applyCap(itemWidths.length);
  }

  return applyCap(countFittingNavItems(itemWidths, moreWidth, availableWidth, gap));
};
